/**
 * Authentication API Service
 * Handles communication with backend authentication endpoints using the unified HTTP client
 */

import { HttpClient } from '@http';
import { env } from '@/env/env';
import { AttributionPayload, AuthError, AuthErrorType, AuthMessageResponse, LoginResponse, RefreshResponse, TelegramAuthData } from '../types';

/**
 * Authentication API service class
 */
export class AuthApiService {
    private readonly httpClient: HttpClient;

    constructor() {
        // Create HTTP client without authentication (to avoid circular dependency)
        this.httpClient = new HttpClient({
            baseUrl: env('NEXT_PUBLIC_API_BASE_URL'),
            timeout: 30000,
            retries: { maxAttempts: 3, baseDelay: 1000, maxDelay: 10000, backoffFactor: 2 },
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });
    }

    /**
     * Login with Google token
     */
    async loginWithGoogle(googleToken: string, attribution?: AttributionPayload): Promise<LoginResponse> {
        try {
            const requestBody: { token: string; attribution?: AttributionPayload } = { token: googleToken };
            if (attribution) {
                requestBody.attribution = attribution;
            }

            const response = await this.httpClient.post<LoginResponse>('/auth/google', requestBody);

            // Validate response structure
            if (!response.data.access_token || !response.data.token_type) {
                throw new AuthError(
                    AuthErrorType.BACKEND_AUTH_ERROR,
                    'Invalid login response format'
                );
            }

            return response.data;
        } catch (error) {
            if (error instanceof AuthError) {
                throw error;
            }

            // Convert HTTP errors to AuthErrors
            throw new AuthError(
                AuthErrorType.BACKEND_AUTH_ERROR,
                'Login failed',
                error
            );
        }
    }

    /**
     * Refresh access token
     */
    async refreshToken(refreshToken: string): Promise<RefreshResponse> {
        try {

            const response = await this.httpClient.post<RefreshResponse>('/auth/refresh', { refresh_token: refreshToken });

            // Validate response structure
            if (!response.data.access_token || !response.data.refresh_token || !response.data.token_type) {
                throw new AuthError(
                    AuthErrorType.BACKEND_AUTH_ERROR,
                    'Invalid refresh response format'
                );
            }

            return response.data;
        } catch (error) {
            // Handle specific HTTP status codes
            if (error instanceof Error && 'status' in error) {
                const status = (error as any).status;
                if (status === 401 || status === 403) {
                    throw new AuthError(
                        AuthErrorType.REFRESH_TOKEN_EXPIRED,
                        'Refresh token expired or invalid'
                    );
                }
            }

            if (error instanceof AuthError) {
                throw error;
            }

            throw new AuthError(
                AuthErrorType.BACKEND_AUTH_ERROR,
                'Token refresh failed',
                error
            );
        }
    }

    /**
     * Logout (revoke tokens on backend)
     */
    async logout(accessToken: string): Promise<void> {
        try {
            await this.httpClient.post('/auth/logout', {}, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
        } catch (error) {
            // Log but don't throw - logout should be handled gracefully
            console.warn('Logout request failed:', error);
        }
    }

    /**
     * Send magic link to email
     */
    async sendMagicLink(email: string, attribution?: AttributionPayload): Promise<AuthMessageResponse> {
        try {
            const requestBody: { email: string; attribution?: AttributionPayload } = { email };
            if (attribution) {
                requestBody.attribution = attribution;
            }

            const response = await this.httpClient.post<AuthMessageResponse>('/auth/magic-link', requestBody);
            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Failed to send magic link', error);
        }
    }

    /**
     * Verify magic link token and get JWT tokens
     */
    async verifyMagicLink(token: string, attribution?: AttributionPayload): Promise<LoginResponse> {
        try {
            const requestBody: { token: string; attribution?: AttributionPayload } = { token };
            if (attribution) {
                requestBody.attribution = attribution;
            }

            const response = await this.httpClient.post<LoginResponse>('/auth/magic-link/verify', requestBody);

            if (!response.data.access_token || !response.data.token_type) {
                throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Invalid magic link verify response');
            }

            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Magic link verification failed', error);
        }
    }

    /**
     * Login with email and password
     */
    async loginWithPassword(email: string, password: string): Promise<LoginResponse> {
        try {
            const response = await this.httpClient.post<LoginResponse>('/auth/login', { email, password });

            if (!response.data.access_token || !response.data.token_type) {
                throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Invalid login response format');
            }

            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Password login failed', error);
        }
    }

    /**
     * Set password for the first time (requires auth)
     */
    async setPassword(password: string, accessToken: string): Promise<AuthMessageResponse> {
        try {
            const response = await this.httpClient.post<AuthMessageResponse>('/auth/password/set', { password }, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Failed to set password', error);
        }
    }

    /**
     * Change existing password (requires auth)
     */
    async changePassword(currentPassword: string, newPassword: string, accessToken: string): Promise<AuthMessageResponse> {
        try {
            const response = await this.httpClient.post<AuthMessageResponse>('/auth/password/change', {
                current_password: currentPassword,
                new_password: newPassword,
            }, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Failed to change password', error);
        }
    }

    /**
     * Request password reset email
     */
    async requestPasswordReset(email: string): Promise<AuthMessageResponse> {
        try {
            const response = await this.httpClient.post<AuthMessageResponse>('/auth/password/reset-request', { email });
            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Failed to request password reset', error);
        }
    }

    /**
     * Reset password using token from email
     */
    async resetPassword(token: string, newPassword: string): Promise<AuthMessageResponse> {
        try {
            const response = await this.httpClient.post<AuthMessageResponse>('/auth/password/reset', {
                token,
                new_password: newPassword,
            });
            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Failed to reset password', error);
        }
    }

    /**
     * Login with Amazon access token (Login with Amazon SDK)
     */
    async loginWithAmazon(accessToken: string, attribution?: AttributionPayload): Promise<LoginResponse> {
        try {
            const requestBody: { token: string; attribution?: AttributionPayload } = { token: accessToken };
            if (attribution) {
                requestBody.attribution = attribution;
            }

            const response = await this.httpClient.post<LoginResponse>('/auth/amazon', requestBody);

            if (!response.data.access_token || !response.data.token_type) {
                throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Invalid Amazon login response');
            }

            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Amazon login failed', error);
        }
    }

    /**
     * Login with Telegram auth data (Telegram Login Widget)
     */
    async loginWithTelegram(data: TelegramAuthData): Promise<LoginResponse> {
        try {
            const response = await this.httpClient.post<LoginResponse>('/auth/telegram', data);

            if (!response.data.access_token || !response.data.token_type) {
                throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Invalid Telegram login response');
            }

            return response.data;
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Telegram login failed', error);
        }
    }

    /**
     * Add email for users without email (e.g., Telegram users)
     */
    async addEmail(email: string, accessToken: string): Promise<void> {
        try {
            await this.httpClient.patch('/users/me', { email }, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
        } catch (error) {
            if (error instanceof AuthError) throw error;
            throw new AuthError(AuthErrorType.BACKEND_AUTH_ERROR, 'Failed to add email', error);
        }
    }
}

/**
 * Singleton Auth API service instance
 */
let authApiService: AuthApiService | null = null;

/**
 * Get or create Auth API service instance
 */
export function getAuthApiService(): AuthApiService {
    if (!authApiService) {
        authApiService = new AuthApiService();
    }
    return authApiService;
}
